import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, Trash2, Download, Trophy } from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from "recharts";

const fmtNum = (n) => new Intl.NumberFormat("es-ES").format(Math.round(n || 0));
const fmtEur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

function ProvinceBars({ rows, nationalAvg, groupLabel }) {
  const sorted = [...rows].sort((a, b) => a.province.localeCompare(b.province, "es"));
  const data = sorted.map((r) => ({
    province: r.province,
    pct: r.avg_generic_pct,
    studies: r.studies,
  }));
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 overflow-x-auto" data-testid="benchmark-bars">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-3">
        Porcentaje medio de dispensación de genéricos por {groupLabel}
      </div>
      <div style={{ minWidth: Math.max(900, sorted.length * 36) }}>
        <ResponsiveContainer width="100%" height={420}>
          <ComposedChart data={data} margin={{ top: 12, right: 24, bottom: 96, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis
              dataKey="province"
              tick={{ fontSize: 11, fill: "#475569" }}
              interval={0}
              angle={-40}
              textAnchor="end"
            />
            <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 100]} stroke="#0F172A" fontSize={11} />
            <Tooltip
              formatter={(value, _name, item) => [
                `${Number(value).toFixed(2)}%`,
                `% Genérico · ${item?.payload?.studies ?? 0} estudios`,
              ]}
              labelStyle={{ color: "#0F172A", fontWeight: "bold" }}
              contentStyle={{ borderRadius: 8, fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
            <ReferenceLine
              y={nationalAvg}
              stroke="#0F172A"
              strokeDasharray="6 3"
              strokeWidth={1.5}
              label={{
                value: `Media nacional ${nationalAvg.toFixed(1)}%`,
                position: "insideTopRight",
                fill: "#0F172A",
                fontSize: 10,
                fontWeight: 700,
              }}
            />
            <Bar dataKey="pct" name="% Genérico" radius={[3, 3, 0, 0]}>
              {data.map((d, i) => (
                <Cell key={i} fill={d.pct >= nationalAvg ? "#10B981" : "#F87171"} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-4 text-[10px] text-slate-500 mt-2">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-500 rounded-sm" /> ≥ media nacional ({nationalAvg.toFixed(1)}%)</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-rose-300 rounded-sm" /> &lt; media nacional</span>
      </div>
    </div>
  );
}

function Scatter({ data, national }) {
  if (!data || data.length === 0) {
    return <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-400 text-sm">Sin datos para representar</div>;
  }
  const W = 1000, H = 380, padL = 60, padR = 20, padT = 20, padB = 50;
  // Eje X: cap al p95 para que outliers no aplasten la nube
  const xs = data.map((d) => d.x_annual_euros).filter((v) => v > 0).sort((a, b) => a - b);
  const p95 = xs.length ? xs[Math.floor(xs.length * 0.95)] : 100;
  // Asegurar que el punto de la propia farmacia siempre cabe
  const selfMax = Math.max(...data.filter((d) => d.is_self).map((d) => d.x_annual_euros), 0);
  const maxX = Math.max(p95, selfMax * 1.1, 100);
  const xScale = (v) => padL + (Math.min(v, maxX) / maxX) * (W - padL - padR);
  const yScale = (v) => H - padB - (Math.max(0, Math.min(100, v)) / 100) * (H - padT - padB);

  const colorFor = (p) => {
    if (p.is_self) return { fill: "#0F172A", stroke: "#FBBF24", r: 7 };
    if (p.source === "system") return { fill: "#F87171", stroke: "none", r: 4 };
    return { fill: "#94A3B8", stroke: "none", r: 3 }; // external
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 overflow-x-auto" data-testid="benchmark-scatter">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: "800px" }}>
        {[0, 25, 50, 75, 100].map((p) => (
          <g key={p}>
            <line x1={padL} x2={W - padR} y1={yScale(p)} y2={yScale(p)} stroke="#E2E8F0" strokeDasharray="2 2" />
            <text x={padL - 8} y={yScale(p) + 3} textAnchor="end" fontSize="9" fill="#64748B">{p}%</text>
          </g>
        ))}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const x = padL + f * (W - padL - padR);
          const v = Math.round(maxX * f);
          return (
            <g key={f}>
              <line x1={x} x2={x} y1={padT} y2={H - padB} stroke="#E2E8F0" strokeDasharray="2 2" />
              <text x={x} y={H - padB + 14} textAnchor="middle" fontSize="9" fill="#64748B">{fmtEur(v)}</text>
            </g>
          );
        })}
        {national?.avg_generic_pct != null && (
          <line
            x1={padL} x2={W - padR}
            y1={yScale(national.avg_generic_pct)} y2={yScale(national.avg_generic_pct)}
            stroke="#0F172A" strokeWidth="1.5" strokeDasharray="6 3"
          />
        )}
        {data.filter((d) => !d.is_self).map((p, i) => {
          const c = colorFor(p);
          return (
            <circle
              key={"o" + i}
              cx={xScale(p.x_annual_euros)} cy={yScale(p.y_generic_pct)}
              r={c.r} fill={c.fill} opacity={0.55}
            />
          );
        })}
        {data.filter((d) => d.is_self).map((p, i) => {
          const c = colorFor(p);
          return (
            <circle
              key={"s" + i}
              cx={xScale(p.x_annual_euros)} cy={yScale(p.y_generic_pct)}
              r={c.r} fill={c.fill} stroke={c.stroke} strokeWidth="2"
            />
          );
        })}
        <text x={(W - padR + padL) / 2} y={H - 10} textAnchor="middle" fontSize="11" fill="#475569">Importe anualizado (€)</text>
        <text x={15} y={H / 2} textAnchor="middle" fontSize="11" fill="#475569" transform={`rotate(-90 15 ${H / 2})`}>% genérico</text>
      </svg>
      <div className="flex items-center gap-4 flex-wrap text-[10px] text-slate-500 mt-2">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-slate-400 rounded-full" /> Histórico (anónimo)</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-rose-300 rounded-full" /> Otras farmacias del sistema</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-slate-950 border-2 border-amber-400 rounded-full" /> Tu farmacia</span>
        <span className="flex items-center gap-1.5"><span className="w-4 h-px border-t border-dashed border-slate-900" /> Media nacional ({national?.avg_generic_pct?.toFixed(1)}%)</span>
      </div>
    </div>
  );
}

/* ---------- Scatter por provincia (una provincia a la vez) ---------- */
function ProvinceScatter({ data, national }) {
  // Provincias disponibles (excluye null/vacías)
  const provinces = useMemo(() => {
    const set = new Set();
    (data || []).forEach((p) => { if (p.province) set.add(p.province); });
    return [...set].sort((a, b) => a.localeCompare(b, "es"));
  }, [data]);

  const selfProvince = (data || []).find((p) => p.is_self)?.province || null;
  const [selected, setSelected] = useState(selfProvince || provinces[0] || "");

  useEffect(() => {
    // Si cambia el dataset y la provincia seleccionada ya no existe, reasignar
    if (!selected && provinces.length) {
      setSelected(selfProvince || provinces[0]);
    } else if (selected && !provinces.includes(selected) && provinces.length) {
      setSelected(selfProvince || provinces[0]);
    }
  }, [provinces, selfProvince]);

  const provData = useMemo(
    () => (data || []).filter((p) => p.province === selected),
    [data, selected]
  );

  if (!provinces.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-400 text-sm">
        Sin datos provinciales disponibles.
      </div>
    );
  }

  const W = 1000, H = 380, padL = 60, padR = 20, padT = 20, padB = 50;
  const xs = provData.map((d) => d.x_annual_euros).filter((v) => v > 0).sort((a, b) => a - b);
  const p95 = xs.length ? xs[Math.floor(xs.length * 0.95)] : 100;
  const selfMax = Math.max(...provData.filter((d) => d.is_self).map((d) => d.x_annual_euros), 0);
  const maxX = Math.max(p95, selfMax * 1.1, 100);
  const xScale = (v) => padL + (Math.min(v, maxX) / maxX) * (W - padL - padR);
  const yScale = (v) => H - padB - (Math.max(0, Math.min(100, v)) / 100) * (H - padT - padB);

  // Media provincial de la provincia seleccionada
  const provAvg = provData.length
    ? provData.reduce((acc, p) => acc + (p.y_generic_pct || 0), 0) / provData.length
    : 0;

  const otherPts = provData.filter((d) => !d.is_self);
  const selfPts = provData.filter((d) => d.is_self);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 overflow-x-auto" data-testid="province-scatter">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">
            Nube de farmacias · <span className="text-slate-800">{selected || "—"}</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {provData.length} farmacias en esta provincia · media provincial {provAvg.toFixed(1)}%
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-xs">
          <span className="text-slate-500 font-semibold uppercase tracking-wider text-[10px]">Provincia</span>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            data-testid="province-scatter-select"
            className="px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white min-w-[180px]"
          >
            {provinces.map((p) => (
              <option key={p} value={p}>{p}{selfProvince === p ? " (tu farmacia)" : ""}</option>
            ))}
          </select>
        </label>
      </div>

      {provData.length === 0 ? (
        <div className="p-8 text-center text-slate-400 text-sm">Sin farmacias en esta provincia.</div>
      ) : (
        <>
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: "800px" }}>
            {[0, 25, 50, 75, 100].map((p) => (
              <g key={p}>
                <line x1={padL} x2={W - padR} y1={yScale(p)} y2={yScale(p)} stroke="#E2E8F0" strokeDasharray="2 2" />
                <text x={padL - 8} y={yScale(p) + 3} textAnchor="end" fontSize="9" fill="#64748B">{p}%</text>
              </g>
            ))}
            {[0, 0.25, 0.5, 0.75, 1].map((f) => {
              const x = padL + f * (W - padL - padR);
              const v = Math.round(maxX * f);
              return (
                <g key={f}>
                  <line x1={x} x2={x} y1={padT} y2={H - padB} stroke="#E2E8F0" strokeDasharray="2 2" />
                  <text x={x} y={H - padB + 14} textAnchor="middle" fontSize="9" fill="#64748B">{fmtEur(v)}</text>
                </g>
              );
            })}
            {/* Línea media nacional (referencia) */}
            {national?.avg_generic_pct != null && (
              <line
                x1={padL} x2={W - padR}
                y1={yScale(national.avg_generic_pct)} y2={yScale(national.avg_generic_pct)}
                stroke="#0F172A" strokeWidth="1.2" strokeDasharray="6 3"
              />
            )}
            {/* Línea media provincial */}
            <line
              x1={padL} x2={W - padR}
              y1={yScale(provAvg)} y2={yScale(provAvg)}
              stroke="#2563EB" strokeWidth="1.5" strokeDasharray="4 2"
            />
            {/* Otros puntos (grises) */}
            {otherPts.map((p, i) => (
              <circle
                key={"o" + i}
                cx={xScale(p.x_annual_euros)} cy={yScale(p.y_generic_pct)}
                r={4} fill="#94A3B8" opacity={0.6}
              />
            ))}
            {/* Punto propio rojo destacado */}
            {selfPts.map((p, i) => (
              <g key={"s" + i}>
                <circle
                  cx={xScale(p.x_annual_euros)} cy={yScale(p.y_generic_pct)}
                  r={10} fill="#EF4444" opacity={0.25}
                />
                <circle
                  cx={xScale(p.x_annual_euros)} cy={yScale(p.y_generic_pct)}
                  r={6} fill="#EF4444" stroke="#7F1D1D" strokeWidth="2"
                />
              </g>
            ))}
            <text x={(W - padR + padL) / 2} y={H - 10} textAnchor="middle" fontSize="11" fill="#475569">Importe anualizado (€)</text>
            <text x={15} y={H / 2} textAnchor="middle" fontSize="11" fill="#475569" transform={`rotate(-90 15 ${H / 2})`}>% genérico</text>
          </svg>
          <div className="flex items-center gap-4 flex-wrap text-[10px] text-slate-500 mt-2">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-slate-400 rounded-full" /> Otras farmacias de {selected}</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-rose-500 border-2 border-rose-900 rounded-full" /> Tu farmacia</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-px border-t border-dashed border-blue-600" /> Media provincial ({provAvg.toFixed(1)}%)</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-px border-t border-dashed border-slate-900" /> Media nacional ({national?.avg_generic_pct?.toFixed(1)}%)</span>
          </div>
        </>
      )}
    </div>
  );
}

function ExternalAdminPanel({ count, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [replace, setReplace] = useState(true);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("replace", replace);
      const { data } = await api.post("/admin/external-studies/upload", fd);
      toast.success(`${data.inserted} estudios insertados (${data.skipped} omitidos)`);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cargar");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearAll = async () => {
    if (!window.confirm("¿Borrar TODO el histórico externo cargado?")) return;
    try {
      const { data } = await api.delete("/admin/external-studies");
      toast.success(`${data.deleted} estudios borrados`);
      onChange();
    } catch (err) {
      toast.error("Error");
    }
  };

  return (
    <div className="bg-amber-50/40 border-2 border-amber-200 rounded-lg p-4 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="external-admin-panel">
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold mb-0.5">Admin · Datos externos</div>
        <div className="text-sm text-slate-700">
          Histórico cargado: <span className="font-mono-num font-bold">{count}</span> estudios
        </div>
      </div>
      <label className="text-xs text-slate-700 inline-flex items-center gap-1.5">
        <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
        Reemplazar histórico anterior
      </label>
      <input ref={fileRef} type="file" accept=".xls,.xlsx,.csv,.txt" className="hidden" onChange={handleFile} />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid="upload-external"
        className="inline-flex items-center gap-2 bg-slate-950 text-white px-3 py-2 rounded-md text-xs font-medium hover:bg-slate-800 disabled:opacity-50"
      >
        <Upload className="h-3 w-3" />
        {uploading ? "Cargando…" : "Cargar Excel/CSV"}
      </button>
      {count > 0 && (
        <button
          onClick={clearAll}
          data-testid="clear-external"
          className="inline-flex items-center gap-1.5 bg-white border border-rose-200 text-rose-700 px-3 py-2 rounded-md text-xs font-medium hover:bg-rose-50"
        >
          <Trash2 className="h-3 w-3" />
          Vaciar
        </button>
      )}
    </div>
  );
}

function SelfPositionCard({ position, pharmacyName }) {
  const cardRef = useRef(null);
  const [unit, setUnit] = useState("eur"); // "eur" (default) | "uds"
  if (!position || !position.ccaa) return null;
  const {
    self_generic_pct, self_generic_pct_units, ccaa, ccaa_studies, ccaa_avg_generic_pct,
    ccaa_percentile, ccaa_rank, national_percentile, national_rank, national_universe,
  } = position;
  const displayPct = unit === "uds" && self_generic_pct_units != null ? self_generic_pct_units : self_generic_pct;
  const unitsAvailable = self_generic_pct_units != null;

  const diffVsCcaa = self_generic_pct - (ccaa_avg_generic_pct || 0);
  const winning = diffVsCcaa >= 0;

  // Medalla por percentil CCAA
  const medal = ccaa_percentile == null
    ? null
    : ccaa_percentile >= 90
    ? { emoji: "🥇", label: "Top 10%" }
    : ccaa_percentile >= 75
    ? { emoji: "🥈", label: "Top 25%" }
    : ccaa_percentile >= 50
    ? { emoji: "🥉", label: "Mejor que la media" }
    : null;

  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        backgroundColor: "#0F172A",
        cacheBust: true,
      });
      const link = document.createElement("a");
      link.download = `farmaclass-posicion-${ccaa.toLowerCase().replace(/\s+/g, "-")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Imagen descargada. ¡Compártela en WhatsApp!");
    } catch (e) {
      toast.error("No se pudo generar la imagen");
    }
  };

  return (
    <div className="mb-6" data-testid="self-position-card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Tu posición</div>
        <div className="flex items-center gap-2">
          {unitsAvailable && (
            <div className="inline-flex bg-slate-100 rounded-md p-0.5 text-[10px] uppercase tracking-wider font-bold">
              <button
                onClick={() => setUnit("eur")}
                data-testid="self-pct-eur"
                className={`px-2.5 py-1 rounded ${unit === "eur" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
              >
                €
              </button>
              <button
                onClick={() => setUnit("uds")}
                data-testid="self-pct-uds"
                className={`px-2.5 py-1 rounded ${unit === "uds" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
                title="Métrica informativa (no afecta al ranking)"
              >
                Uds
              </button>
            </div>
          )}
          <button
            onClick={handleDownload}
            data-testid="share-position-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 bg-white border border-slate-200 px-3 py-1.5 rounded-md hover:border-slate-400 transition-colors"
          >
            <Download className="h-3 w-3" />
            Descargar imagen
          </button>
        </div>
      </div>

      <div
        ref={cardRef}
        className="relative overflow-hidden rounded-2xl text-white"
        style={{
          background: "linear-gradient(135deg, #0F172A 0%, #1E293B 60%, #0F172A 100%)",
          minHeight: 280,
        }}
      >
        {/* Decoración */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 70%, #fff 1px, transparent 1px)",
          backgroundSize: "40px 40px, 60px 60px",
        }} />
        <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full" style={{ background: "radial-gradient(circle, rgba(251,191,36,0.18) 0%, transparent 65%)" }} />

        <div className="relative p-7 lg:p-9 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-7">
          {/* Izquierda: titular */}
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-amber-300/90 font-bold mb-3">
              <Trophy className="h-3 w-3" />
              FarmaClass · Benchmark
            </div>
            <h3 className="font-display text-3xl lg:text-4xl font-bold leading-tight mb-1">
              {pharmacyName || "Tu farmacia"}
            </h3>
            <div className="text-slate-400 text-sm mb-5">
              {ccaa} · {ccaa_studies} farmacias comparadas
            </div>

            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-1">
                % genérico dispensado ({unit === "uds" ? "unidades" : "€"})
              </div>
              <div className="flex items-baseline gap-3">
                <div className="font-display text-6xl font-bold font-mono-num text-white">
                  {displayPct.toFixed(1)}<span className="text-3xl text-slate-400">%</span>
                </div>
                {unit === "eur" && (
                  <div className={`text-sm font-bold ${winning ? "text-emerald-400" : "text-rose-400"}`}>
                    {winning ? "▲" : "▼"} {Math.abs(diffVsCcaa).toFixed(1)} p.p.
                    <div className="text-[10px] text-slate-400 font-normal uppercase tracking-wide">vs media CCAA ({(ccaa_avg_generic_pct || 0).toFixed(1)}%)</div>
                  </div>
                )}
              </div>
              {unit === "uds" && (
                <div className="text-[10px] text-amber-300/80 mt-2 max-w-md leading-relaxed">
                  Vista informativa. El ranking y los percentiles del benchmark se calculan siempre en €.
                </div>
              )}
            </div>
          </div>

          {/* Derecha: percentiles destacados */}
          <div className="flex flex-col gap-4 justify-center lg:border-l lg:border-white/10 lg:pl-7">
            <div data-testid="ccaa-percentile-block">
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-bold mb-1">En {ccaa}</div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-5xl font-bold font-mono-num text-amber-300">
                  {ccaa_percentile != null ? ccaa_percentile.toFixed(0) : "—"}
                </span>
                <span className="text-amber-300/70 text-sm font-bold">percentil</span>
                {medal && (
                  <span
                    className="ml-auto inline-flex items-center gap-1 bg-amber-400/15 border border-amber-300/30 px-2 py-1 rounded-full text-[11px] font-bold text-amber-200"
                    title={medal.label}
                  >
                    <span style={{ fontSize: 18, lineHeight: 1 }}>{medal.emoji}</span>
                    {medal.label}
                  </span>
                )}
              </div>
              <div className="text-slate-300 text-sm mt-0.5">
                Posición <span className="font-bold text-white">#{ccaa_rank}</span> de {ccaa_studies}
              </div>
              <div className="text-[11px] text-slate-400 mt-1">
                Mejor que el <span className="text-emerald-400 font-bold">{(ccaa_percentile || 0).toFixed(0)}%</span> de las farmacias de tu CCAA
              </div>
            </div>

            <div className="border-t border-white/10 pt-3" data-testid="national-percentile-block">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold mb-1">En España</div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-display text-2xl font-bold font-mono-num text-white">
                  #{national_rank}<span className="text-slate-500 text-base font-normal"> / {national_universe}</span>
                </span>
                <span className="ml-auto text-xs text-slate-400">
                  Percentil <span className="text-white font-bold">{(national_percentile || 0).toFixed(0)}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="relative px-7 lg:px-9 py-3 bg-white/[0.03] border-t border-white/5 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
          <span>farmaclass.es · análisis EFG</span>
          <span>{new Date().toLocaleDateString("es-ES", { month: "long", year: "numeric" })}</span>
        </div>
      </div>
    </div>
  );
}

export default function BenchmarkPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [groupBy, setGroupBy] = useState("ccaa");
  const [extCount, setExtCount] = useState(0);

  const reload = () => {
    setLoading(true);
    setLoadError(null);
    Promise.all([
      api.get("/benchmark/provinces"),
      isAdmin ? api.get("/admin/external-studies/count").then((r) => r.data.count).catch(() => 0) : Promise.resolve(0),
    ]).then(([b, c]) => {
      setData(b.data);
      setExtCount(c);
    }).catch((err) => {
      const status = err?.response?.status;
      const isTimeout = status === 524 || status === 502 || status === 504 || err?.code === "ECONNABORTED";
      setLoadError(
        isTimeout
          ? "El servidor tardó demasiado en responder (timeout). Espera unos segundos y pulsa Reintentar."
          : `No se pudieron cargar los datos del benchmark${status ? ` (error ${status})` : ""}. Pulsa Reintentar.`
      );
      setData(null);
    }).finally(() => setLoading(false));
  };
  useEffect(reload, [isAdmin]);

  const rows = useMemo(() => {
    if (!data) return [];
    if (groupBy === "ccaa") {
      return data.ccaa.map((c) => ({ province: c.ccaa, ccaa: c.ccaa, studies: c.studies, avg_generic_pct: c.avg_generic_pct }));
    }
    return data.provinces;
  }, [data, groupBy]);

  if (loading) return <div className="p-12 text-center text-slate-500 text-sm">Cargando benchmark…</div>;

  if (loadError) {
    return (
      <div className="p-8 lg:p-12 max-w-[900px]" data-testid="benchmark-error">
        <header className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Comparativa nacional</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950">Benchmark provincial</h1>
        </header>
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-6">
          <div className="font-semibold text-rose-900 mb-2">No se pudo cargar el benchmark</div>
          <div className="text-sm text-rose-800 mb-4">{loadError}</div>
          <button
            onClick={reload}
            className="bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-md"
            data-testid="benchmark-retry-btn"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const nat = data?.national || {};

  return (
    <div className="p-8 lg:p-12 max-w-[1500px]" data-testid="benchmark-page">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Comparativa nacional</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950">Benchmark provincial</h1>
        <p className="text-slate-500 mt-2 max-w-2xl text-sm">
          Comparativa anónima del <strong>% de dispensación a genérico en euros</strong>. Mezcla histórico externo y estudios reales del sistema. Tu farmacia aparece destacada.
        </p>
      </header>

      {isAdmin && <ExternalAdminPanel count={extCount} onChange={reload} />}

      <SelfPositionCard position={data?.self_position} pharmacyName={user?.pharmacy_name || user?.name} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Estudios totales" value={fmtNum(nat.studies)} />
        <Stat label="Histórico externo" value={fmtNum(nat.external_studies)} />
        <Stat label="% genérico medio nacional" value={`${(nat.avg_generic_pct || 0).toFixed(1)}%`} cls="text-emerald-700" testid="kpi-national-avg" />
        <Stat
          label="Tu % genérico"
          value={nat.self_avg_generic_pct != null ? `${nat.self_avg_generic_pct.toFixed(1)}%` : "—"}
          cls={
            nat.self_avg_generic_pct == null
              ? "text-slate-400"
              : nat.self_avg_generic_pct >= (nat.avg_generic_pct || 0)
              ? "text-emerald-700"
              : "text-rose-700"
          }
          testid="kpi-self-avg"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 bg-slate-100 rounded-md p-1">
          <button onClick={() => setGroupBy("ccaa")} data-testid="group-ccaa" className={`text-xs px-3 py-1.5 rounded ${groupBy === "ccaa" ? "bg-white shadow-sm font-bold" : "text-slate-500"}`}>Por CCAA</button>
          <button onClick={() => setGroupBy("province")} data-testid="group-province" className={`text-xs px-3 py-1.5 rounded ${groupBy === "province" ? "bg-white shadow-sm font-bold" : "text-slate-500"}`}>Por provincia</button>
        </div>
        <div className="text-xs text-slate-500">
          Métrica: <span className="font-bold text-slate-700">% genérico medio</span>
        </div>
      </div>

      <ProvinceBars
        rows={rows}
        nationalAvg={nat.avg_generic_pct || 0}
        groupLabel={groupBy === "ccaa" ? "comunidad autónoma" : "provincia"}
      />

      <h2 className="font-display text-xl font-bold mt-8 mb-3 text-slate-900">Visión global</h2>
      <Scatter data={data?.scatter || []} national={nat} />

      {groupBy === "province" && (
        <>
          <h2 className="font-display text-xl font-bold mt-8 mb-3 text-slate-900">Nube por provincia</h2>
          <p className="text-slate-500 text-sm mb-3 max-w-2xl">
            Elige una provincia para ver cómo se distribuyen las farmacias de esa zona. Tu farmacia aparece destacada en <span className="text-rose-600 font-semibold">rojo</span>.
          </p>
          <ProvinceScatter data={data?.scatter || []} national={nat} />
        </>
      )}

      <div className="mt-6 bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="font-display text-base font-bold">Detalle por {groupBy === "ccaa" ? "comunidad autónoma" : "provincia"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="benchmark-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left p-3">{groupBy === "ccaa" ? "CCAA" : "Provincia"}</th>
                {groupBy === "province" && <th className="text-left p-3">CCAA</th>}
                <th className="text-right p-3">Estudios</th>
                {groupBy === "province" && <th className="text-right p-3">Externos / Sistema</th>}
                <th className="text-right p-3">% genérico medio</th>
                <th className="text-right p-3">vs nacional</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-slate-400">Sin datos.</td></tr>
              )}
              {rows.map((r) => {
                const delta = r.avg_generic_pct - (nat.avg_generic_pct || 0);
                return (
                  <tr key={r.province} className="hover:bg-slate-50">
                    <td className="p-3 font-medium text-slate-900">{r.province}</td>
                    {groupBy === "province" && <td className="p-3 text-slate-600 text-xs">{r.ccaa}</td>}
                    <td className="p-3 text-right font-mono-num">{r.studies}</td>
                    {groupBy === "province" && (
                      <td className="p-3 text-right text-xs text-slate-500 font-mono-num">
                        {r.external_studies ?? 0} / {r.system_studies ?? 0}
                      </td>
                    )}
                    <td className="p-3 text-right font-mono-num font-semibold">{r.avg_generic_pct.toFixed(1)}%</td>
                    <td className={`p-3 text-right font-mono-num font-semibold ${delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {delta >= 0 ? "+" : ""}{delta.toFixed(1)} p.p.
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, cls = "text-slate-900", testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">{label}</div>
      <div className={`font-display text-2xl font-bold font-mono-num ${cls}`}>{value}</div>
    </div>
  );
}
